import { useRef } from 'react'
import type { PanInfo } from 'framer-motion'
import { Reply } from 'lucide-react'
import { motion, useMotionValue, animate, useTransform } from 'framer-motion'

const SWIPE_REPLY_TRIGGER_PX = 52

interface ChatMessageSwipeReplyRowProps {
  /** When false, children render with no swipe behavior. */
  swipeReplyEnabled: boolean
  onSwipeConfirmReply: () => void
  children: React.ReactNode
}

/**
 * Incoming messages: drag the row to the right to start a reply (WhatsApp-style).
 */
export function ChatMessageSwipeReplyRow({
  swipeReplyEnabled,
  onSwipeConfirmReply,
  children,
}: ChatMessageSwipeReplyRowProps) {
  const horizontalDragOffsetPx = useMotionValue(0)
  const replyIconRevealOpacity = useTransform(
    horizontalDragOffsetPx,
    [0, 24, SWIPE_REPLY_TRIGGER_PX],
    [0, 0.45, 1]
  )
  const didFireReplyThisGestureRef = useRef(false)

  if (!swipeReplyEnabled) {
    return <>{children}</>
  }

  return (
    <div className="relative w-full">
      <motion.div
        className="pointer-events-none absolute left-0 top-1/2 z-0 flex h-8 w-8 -translate-y-1/2 items-center justify-center text-primary"
        style={{ opacity: replyIconRevealOpacity }}
        aria-hidden
      >
        <Reply className="h-5 w-5" strokeWidth={2.25} />
      </motion.div>

      <motion.div
        className="relative z-[1]"
        style={{ x: horizontalDragOffsetPx }}
        drag="x"
        dragConstraints={{ left: 0, right: 88 }}
        dragElastic={0.08}
        dragMomentum={false}
        onDragStart={() => {
          didFireReplyThisGestureRef.current = false
        }}
        onDragEnd={(_event: PointerEvent | MouseEvent | TouchEvent, dragGestureInfo: PanInfo) => {
          const offsetPx = horizontalDragOffsetPx.get()
          if (
            !didFireReplyThisGestureRef.current &&
            (offsetPx >= SWIPE_REPLY_TRIGGER_PX || dragGestureInfo.velocity.x > 400)
          ) {
            didFireReplyThisGestureRef.current = true
            onSwipeConfirmReply()
          }
          void animate(horizontalDragOffsetPx, 0, { type: 'spring', stiffness: 520, damping: 38 })
        }}
      >
        {children}
      </motion.div>
    </div>
  )
}
